import { useState } from "react";
import { PlusIcon } from "lucide-react";

import { AddCommissionAdjustmentDialog } from "./AddCommissionAdjustmentDialog";
import { EarningsTrackerCommissionCell } from "./EarningsTrackerCommissionCell";
import { EarningsTrackerRetentionCell } from "./EarningsTrackerRetentionCell";
import { EarningsTrackerStartDateCell } from "./EarningsTrackerStartDateCell";
import { EarningsTrackerStatusCell } from "./EarningsTrackerStatusCell";

import { Column } from "@/components/Column";
import { EmptyDataDash } from "@/components/EmptyDataDash";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Avatar } from "@/components/quick-access/Avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { dates } from "@/lib/dates";
import { earningsTracker } from "@/lib/earningsTracker";
import { formatting } from "@/lib/formatting";
import { cn } from "@/lib/utils";
import {
  EarningsEmploymentStatus,
  EarningsPaymentStatus,
  EarningsPlacement,
} from "@/types";

const employmentStatusToBadge: Record<
  EarningsEmploymentStatus,
  { className: string; label: string }
> = {
  [EarningsEmploymentStatus.NOT_STARTED]: {
    className: "border-slate-200 bg-slate-100 text-slate-700",
    label: "Not started",
  },
  [EarningsEmploymentStatus.STARTED]: {
    className: "border-green-200 bg-green-50 text-green-700",
    label: "Started",
  },
  [EarningsEmploymentStatus.CHURNED]: {
    className: "border-amber-200 bg-amber-50 text-amber-800",
    label: "Churned",
  },
};

export const EarningsTrackerRow = ({
  isAdjustmentAdmin,
  placement,
  viewedUserId,
  viewedYear,
}: {
  isAdjustmentAdmin: boolean;
  placement: EarningsPlacement;
  viewedUserId?: number;
  viewedYear: number;
}) => {
  /* Helpers */
  // Undated rows have no revenue basis to show yet — just the add-date button
  const hasStartDate = earningsTracker.getStartDateIso(placement) !== null;
  // Clawed rows show their pre-clawback worth greyed out — what was lost —
  // instead of a strikethrough down to $0
  const isClawedBack =
    earningsTracker.getPaymentStatus(placement) ===
    EarningsPaymentStatus.CLAWBACK;
  const commissionDollars = isClawedBack
    ? earningsTracker.getPreClawbackDollars(placement)
    : earningsTracker.getCommissionDollars(placement);
  // Live adjusted rows show what was paid struck through next to the current net
  const isAdjusted = !isClawedBack && earningsTracker.hasAdjustments(placement);
  const paidBeforeAdjustments = isAdjusted
    ? earningsTracker.getPaidDollars(placement)
    : null;
  const rateBeforeAdjustments = isAdjusted
    ? earningsTracker.getOriginalRatePercent(placement)
    : null;

  /* Render */
  const render = () => (
    <TableRow
      className={cn(placement.isSuperseded && "bg-slate-50 opacity-60")}
    >
      <CandidateCell placement={placement} />
      <CreditedDateCell placement={placement} viewedYear={viewedYear} />
      <EarningsTrackerStartDateCell placement={placement} />
      <EarningsTrackerRetentionCell placement={placement} />
      <EmploymentStatusCell placement={placement} viewedUserId={viewedUserId} />
      <CurrencyCell
        isDisabled={placement.isSuperseded}
        value={hasStartDate ? placement.revenueEarned : null}
      />
      <EarningsTrackerCommissionCell
        isDisabled={placement.isSuperseded || isClawedBack}
        placement={placement}
        previousValue={paidBeforeAdjustments}
        value={commissionDollars}
      />
      <RateCell
        previousRate={rateBeforeAdjustments}
        rate={placement.commissionRate}
      />
      <EarningsTrackerStatusCell
        placement={placement}
        viewedUserId={viewedUserId}
      />
      <AdjustmentCell
        isAdjustmentAdmin={isAdjustmentAdmin}
        placement={placement}
        viewedUserId={viewedUserId}
      />
    </TableRow>
  );

  return render();
};

const CandidateCell = ({ placement }: { placement: EarningsPlacement }) => {
  /* Render */
  const render = () => (
    <TableCell>
      <Column className="min-w-0 gap-0.5">
        <Text className="truncate text-sm font-medium text-slate-900">
          {placement.candidateName}
        </Text>
        <Text className="truncate text-xs font-normal text-slate-500">
          {placement.jobTitle}
        </Text>
        <Text className="truncate text-xs font-normal text-slate-500">
          {placement.companyName}
        </Text>
      </Column>
    </TableCell>
  );

  return render();
};

const CreditedDateCell = ({
  placement,
  viewedYear,
}: {
  placement: EarningsPlacement;
  viewedYear: number;
}) => {
  /* Helpers */
  const formattedCreditedDate = dates.formatIsoDateForUser(
    placement.creditedDate,
  );
  // Backfills earn under the original placement's date; surface their own too
  const isCreditedElsewhere =
    placement.isBackfill && placement.creditedDate !== placement.dateAccepted;
  // Out-of-year rows resurfaced by a clawback carry their own year
  const originYear = earningsTracker.getOriginYear(placement, viewedYear);

  /* Render */
  const render = () => {
    if (!formattedCreditedDate) {
      return (
        <TableCell>
          <EmptyDataDash />
        </TableCell>
      );
    }

    return (
      <TableCell>
        <Column className="gap-0.5">
          <Text className="whitespace-nowrap text-sm font-normal text-slate-700">
            {formattedCreditedDate}
          </Text>
          {isCreditedElsewhere ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Text className="w-fit cursor-default whitespace-nowrap text-xs font-normal text-slate-400">
                  Backfill · {dates.formatIsoDateForUser(placement.dateAccepted)}
                </Text>
              </TooltipTrigger>
              <TooltipContent>
                Backfills are credited under the original placement&apos;s date
              </TooltipContent>
            </Tooltip>
          ) : null}
          {originYear !== null ? (
            <Text className="whitespace-nowrap text-xs font-normal text-slate-500">
              From {String(originYear)}
            </Text>
          ) : null}
        </Column>
      </TableCell>
    );
  };

  return render();
};

const AdjustmentCell = ({
  isAdjustmentAdmin,
  placement,
  viewedUserId,
}: {
  isAdjustmentAdmin: boolean;
  placement: EarningsPlacement;
  viewedUserId?: number;
}) => {
  const { userNameMap } = useTaggableUsers();

  /* React Hooks */
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  /* Helpers */
  const ledgerNetDollars = placement.ledgerEntries.reduce(
    (total, entry) => total + entry.amount,
    0,
  );
  const stafferName =
    viewedUserId !== undefined ? userNameMap[viewedUserId] : "this staffer";

  /* Render */
  const render = () => {
    if (!isAdjustmentAdmin) {
      return <TableCell />;
    }

    return (
      <TableCell>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              className="h-7 w-7 shrink-0 rounded-full border border-slate-300 bg-white p-0 text-slate-600 shadow-sm hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900"
              onClick={() => {
                setIsDialogOpen(true);
              }}
            >
              <PlusIcon className="h-3.5 w-3.5 shrink-0 opacity-70" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Add commission adjustment</TooltipContent>
        </Tooltip>
        <AddCommissionAdjustmentDialog
          ledgerNetDollars={ledgerNetDollars}
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          placement={placement}
          stafferName={stafferName}
        />
      </TableCell>
    );
  };

  return render();
};

const EmploymentStatusCell = ({
  placement,
  viewedUserId,
}: {
  placement: EarningsPlacement;
  viewedUserId?: number;
}) => {
  const { userInitialsMap, userNameMap } = useTaggableUsers();

  /* Helpers */
  const badge =
    employmentStatusToBadge[earningsTracker.getEmploymentStatus(placement)];
  const isChurned =
    earningsTracker.getEmploymentStatus(placement) ===
    EarningsEmploymentStatus.CHURNED;

  /* Render */
  const renderSubtext = () => {
    if (placement.isSuperseded && placement.backfilledByUserId !== null) {
      if (placement.backfilledByUserId === viewedUserId) {
        return (
          <Text className="text-xs font-normal text-slate-500">
            Backfilled yourself
          </Text>
        );
      }

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Row className="cursor-default items-center gap-1">
              <Text className="text-xs font-normal text-slate-500">
                Backfilled by
              </Text>
              <Avatar
                initials={userInitialsMap[placement.backfilledByUserId]}
              />
            </Row>
          </TooltipTrigger>
          <TooltipContent>
            {userNameMap[placement.backfilledByUserId]}
          </TooltipContent>
        </Tooltip>
      );
    }

    if (isChurned) {
      return (
        <Text className="text-xs font-normal text-slate-500">
          Backfill needed
        </Text>
      );
    }

    return null;
  };

  const render = () => (
    <TableCell>
      <Column className="items-start gap-1">
        <Badge
          variant="outline"
          className={cn(
            "h-6 whitespace-nowrap py-0 font-medium",
            badge.className,
          )}
        >
          {badge.label}
        </Badge>
        {renderSubtext()}
      </Column>
    </TableCell>
  );

  return render();
};

const CurrencyCell = ({
  isDisabled,
  value,
}: {
  isDisabled: boolean;
  value: number | null;
}) => {
  /* Render */
  const render = () => {
    if (value === null) {
      return (
        <TableCell>
          <EmptyDataDash />
        </TableCell>
      );
    }

    return (
      <TableCell>
        <Text
          className={cn(
            "text-sm font-medium tabular-nums",
            isDisabled ? "text-slate-400" : "text-slate-900",
          )}
        >
          {formatting.currency(value)}
        </Text>
      </TableCell>
    );
  };

  return render();
};

const RateCell = ({
  previousRate,
  rate,
}: {
  previousRate: number | null;
  rate: number | null;
}) => {
  /* Render */
  const render = () => {
    if (rate === null) {
      return (
        <TableCell>
          <EmptyDataDash />
        </TableCell>
      );
    }

    return (
      <TableCell>
        <Row className="items-center gap-2">
          {previousRate !== null && previousRate !== rate ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Text className="cursor-default whitespace-nowrap text-sm font-medium tabular-nums text-slate-400 line-through">
                  {String(previousRate)}%
                </Text>
              </TooltipTrigger>
              <TooltipContent>
                Commission rate changed due to clawback
              </TooltipContent>
            </Tooltip>
          ) : null}
          <Text className="whitespace-nowrap text-sm font-medium tabular-nums text-slate-900">
            {String(rate)}%
          </Text>
        </Row>
      </TableCell>
    );
  };

  return render();
};
